"use client";

// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { hexToRgba } from "@/utils/utils";
import Image from "next/image";
import { useMemo, useRef, useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import RevealOnScroll from "@/components/atoms/RevealOnScroll";

interface MetaItem {
  id?: string | number;
  meta_title?: string;
  meta_title_line_1?: string;
  meta_title_line_2?: string;
  meta_description?: string;
  meta_priority?: number;
  meta_title_text_color?: string;
  meta_title_text_color_line_1?: string;
  meta_title_text_color_line_2?: string;
  meta_title_highlight_color?: string;
  meta_description_text_color?: string;
  meta_icon?: string;
  meta_alt_icon?: string;
  meta_status?: boolean;
}

const DigitalLearning1 = ({ data }: { data: any }) => {
  const { data: sectionData } = data || {};
  const ref = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isMobile = useIsMobile(1080);
  const [isPlaying, setIsPlaying] = useState(true);
  const [videoMuted, setVideoMuted] = useState(true);
  const dispatch = useDispatch();

  if (!sectionData?.status) {
    return null;
  }

  const {
    desktop_background_color = "#FFFFFF",
    mobile_background_color = "#FFFFFF",
    backvariant = "variant_3",
    gradient_color_1 = "rgba(0, 212, 235, 0.33)",
    gradient_color_2 = "rgba(0, 0, 0, 0.66)",
    gradient_color_3 = "rgba(0, 0, 0, 0.66)",
    gradient_color_4 = "rgba(83, 69, 195, 0.46)",
    pre_title = "",
    pre_title_color = "rgba(34, 34, 34, 0.8)",
    title = "",
    title_start_color = "#342B7C",
    title_end_color = "#02A7B6",
    title_gradient_angle = "90deg",
    description = "",
    description_color = "rgba(34, 34, 34, 0.8)",
    image = "",
    alt_image = "",
    video = "",
    cta_type = "lead_drop",
    cta_link = "",
    cta_text = "",
    cta_text_color = "#FFFFFF",
    cta_backcolor_start_color = "#02A7B6",
    cta_backcolor_end_color = "#342B7C",
    cta_gradient_angle = "90deg",
    cta_icon = "",
    alt_cta_icon = "",
    metadata = [],
    enable_animation = false,
  } = sectionData;

  const isAnimationEnabled =
    enable_animation === true ||
    enable_animation === 1 ||
    enable_animation === "1";

  const sortedMetadata = useMemo(() => {
    if (!metadata || metadata.length === 0) return [];
    return [...metadata]
      .filter((item: MetaItem) => item?.meta_status !== false)
      .sort(
        (a: MetaItem, b: MetaItem) =>
          (a.meta_priority ?? 0) - (b.meta_priority ?? 0)
      );
  }, [metadata]);

  const toRgba = (color: string, fallbackOpacity: number): string => {
    if (!color) return `rgba(0, 0, 0, ${fallbackOpacity})`;
    if (color.startsWith("rgba(") || color.startsWith("rgb(")) return color;
    return hexToRgba(color, fallbackOpacity);
  };

  const getGradientBackground = () => {
    const g1 = toRgba(gradient_color_1, 0.33);
    const g2 = toRgba(gradient_color_2, 0.66);
    const g3 = toRgba(gradient_color_3, 0.66);
    const g4 = toRgba(gradient_color_4, 0.46);
    const base =
      isMobile === true ? mobile_background_color : desktop_background_color;
    switch (backvariant) {
      case "variant_1":
        return `linear-gradient(253deg, ${g1} 0%, ${g2} 24.68%, ${g3} 79.09%, ${g4} 100%), ${base}`;
      case "variant_2":
        return `linear-gradient(292deg, ${g1} 0%, ${g2} 24.68%, ${g3} 79.09%, ${g4} 100%), ${base}`;
      case "variant_3":
        return base;
      default:
        return base;
    }
  };

  const titleGradientStyle: React.CSSProperties =
    title_start_color && title_end_color
      ? {
          backgroundImage: `linear-gradient(${title_gradient_angle}, ${title_start_color}, ${title_end_color})`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          fontFamily: "var(--font-merriweather)",
        }
      : {
          backgroundImage: `radial-gradient(248.29% 125.44% at 6.23% 19.77%, ${title_start_color || "#342B7C"} 0%, ${title_end_color || "#02A7B6"} 100%)`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          fontFamily: "var(--font-merriweather)",
        };

  const ctaBackgroundStyle: React.CSSProperties =
    cta_backcolor_start_color && cta_backcolor_end_color
      ? {
          background: `linear-gradient(${cta_gradient_angle || "90deg"}, ${cta_backcolor_start_color}, ${cta_backcolor_end_color})`,
        }
      : cta_backcolor_start_color
        ? { backgroundColor: cta_backcolor_start_color }
        : {
            background:
              "linear-gradient(180deg, #02A7B6 50%, #0F88A8 63%, #1B6999 75%, #284A8B 88%, #342B7C 100%)",
          };

  const handleCtaClick = useCallback(
    (e: React.MouseEvent) => {
      if (cta_type === "lead_drop") {
        e.preventDefault();
        dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
      }
    },
    [cta_type, dispatch]
  );

  const handleVideoToggle = useCallback(() => {
    if (!videoRef.current) return;
    const el = videoRef.current;
    if (videoMuted) {
      el.muted = false;
      setVideoMuted(false);
      void el.play();
      setIsPlaying(true);
      return;
    }
    if (isPlaying) {
      el.pause();
    } else {
      void el.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying, videoMuted]);

  useEffect(() => {
    if (!video || isMobile === null) return;

    let cancelled = false;
    let observer: IntersectionObserver | null = null;
    let unmutedOk = false;
    const sectionVisibleRef = { current: false };
    const documentActivatedRef = { current: false };

    const tryUnmuteIfReady = () => {
      if (cancelled || unmutedOk) return;
      const v = videoRef.current;
      if (!v || !sectionVisibleRef.current || !documentActivatedRef.current) return;

      v.muted = false;
      v.play()
        .then(() => {
          if (cancelled) return;
          unmutedOk = true;
          setVideoMuted(false);
        })
        .catch(() => {
          v.muted = true;
          if (!cancelled) setVideoMuted(true);
          return v.play();
        });
    };

    const onDocumentActivate = () => {
      documentActivatedRef.current = true;
      tryUnmuteIfReady();
      window.removeEventListener("pointerdown", onDocumentActivate, true);
      window.removeEventListener("touchstart", onDocumentActivate, true);
      window.removeEventListener("keydown", onDocumentActivate, true);
    };

    window.addEventListener("pointerdown", onDocumentActivate, true);
    window.addEventListener("touchstart", onDocumentActivate, {
      capture: true,
      passive: true,
    });
    window.addEventListener("keydown", onDocumentActivate, true);

    let rafAttempts = 0;
    const connectObserver = () => {
      if (cancelled) return;
      const sectionEl = ref.current;
      const v = videoRef.current;
      if (!sectionEl || !v) {
        if (rafAttempts++ < 90) requestAnimationFrame(connectObserver);
        return;
      }

      observer = new IntersectionObserver(
        (entries) => {
          const hit = entries.some((e) => e.isIntersecting);
          sectionVisibleRef.current = hit;
          if (hit) tryUnmuteIfReady();
        },
        { threshold: 0.25, rootMargin: "0px 0px -8% 0px" }
      );
      observer.observe(sectionEl);
    };

    connectObserver();

    return () => {
      cancelled = true;
      observer?.disconnect();
      window.removeEventListener("pointerdown", onDocumentActivate, true);
      window.removeEventListener("touchstart", onDocumentActivate, true);
      window.removeEventListener("keydown", onDocumentActivate, true);
    };
  }, [video, isMobile]);

  const getMetaTitleLine1 = (item: MetaItem) =>
    item.meta_title_line_1 || item.meta_title || "";

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "digital_learning_section");
  //   }
  // }, []);

  if (isMobile === null) {
    return null;
  }

  const hasFeatureTitle = (item: MetaItem) =>
    !!(getMetaTitleLine1(item) || item.meta_title_line_2);

  const descriptionBlock = description && (
    <RevealOnScroll enableAnimation={isAnimationEnabled}>
    <p
      className={cn(
        "leading-[1.49] w-full",
        "text-[14px]",
        "sm:text-[14px]",
        "md:text-[16px]",
        "lg:text-[18px]"
      )}
      style={{
        color: isMobile ? "rgba(0, 0, 0, 0.7)" : description_color,
        fontFamily: "var(--font-google-sans)",
        fontWeight: 600,
      }}
    >
      {description}
    </p>
    </RevealOnScroll>
  );

  const mediaBlock = (image || video) && (
    <RevealOnScroll
      enableAnimation={isAnimationEnabled}
      y={100}
      x={100}
      scaleStart={0.8}
      className="w-full"
    >
      <div className="flex items-center justify-center w-full">
        <div
          className={cn(
            "relative overflow-hidden w-full",
            "rounded-[10px] shadow-[0px_4px_40px_0px_rgba(23,116,158,0.2)]",
            "sm:rounded-[20px] sm:w-[740px] sm:h-[416px]",
            "md:rounded-[30px] md:w-[800px] md:h-[450px]",
            "lg:rounded-[40px] lg:w-[1130px] lg:h-[635px]"
          )}
          style={{ aspectRatio: isMobile ? "335 / 188" : undefined }}
        >
          {video ? (
            <video
              ref={videoRef}
              className="absolute inset-0 w-full h-full object-cover"
              autoPlay
              loop
              muted={videoMuted}
              playsInline
              poster={image || undefined}
              onClick={handleVideoToggle}
              data-cta
              data-cta-name="video_play"
            >
              <source src={video} />
            </video>
          ) : (
            <Image
              src={image}
              alt={alt_image || "Digital learning"}
              fill
              className="object-cover"
              unoptimized
            />
          )}
        </div>
      </div>
    </RevealOnScroll>
  );

  return (
    <section
      ref={ref}
      className="relative w-full overflow-hidden"
      style={{
        backgroundColor:
          isMobile === true
            ? mobile_background_color
            : desktop_background_color,
      }}
    >
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @media (min-width: 1748px) {
          .digital-learning-1-container {
            max-width: 1750px !important;
            padding-left: 180px !important;
            padding-right: 180px !important;
            padding-top: 80px !important;
            padding-bottom: 80px !important;
          }
        }
      `,
        }}
      />
      {backvariant !== "variant_3" && (
        <div
          className="absolute inset-0 z-0"
          style={{ background: getGradientBackground() }}
        />
      )}
      <div
        className={cn(
          "relative z-10 mx-auto flex flex-col items-center digital-learning-1-container",
          "px-[20px] py-[40px] gap-[24px]",
          "sm:max-w-[1080px] sm:px-[60px] sm:py-[40px] sm:gap-[40px]",
          "md:max-w-[1230px] md:px-[80px] md:py-[60px] md:gap-[60px]",
          "lg:max-w-[1430px] lg:px-[100px] lg:py-[60px] lg:gap-[60px]",
          "xl:max-w-[1750px] xl:px-[180px] xl:py-[80px] xl:gap-[60px]"
        )}
      >
        {/* Mobile: Header (no description) → Media → Description
            Desktop: Header + Description → Media */}

        {/* Header */}
        <div className={cn("flex flex-col items-start w-full", "gap-[4px] sm:gap-[2px]")}>
          {pre_title && (
            <RevealOnScroll enableAnimation={isAnimationEnabled}>
            <p
              className="dark-title-wf w-full line-clamp-1"
              style={{ color: pre_title_color }}
            >
              {pre_title}
            </p>
            </RevealOnScroll>
          )}
          {title && (
            <RevealOnScroll enableAnimation={isAnimationEnabled}>
            <p
              className="gradient-title-denton-blue w-full"
              style={titleGradientStyle}
            >
              {title}
            </p>
            </RevealOnScroll>
          )}

          {/* Desktop: description sits inside header block */}
          {!isMobile && description && (
            <div className="mt-[22px] w-full">{descriptionBlock}</div>
          )}
        </div>

        {/* Media */}
        {mediaBlock}

        {/* Mobile: description appears after media */}
        {isMobile && descriptionBlock}

        {/* Feature Points */}
        {sortedMetadata.length > 0 && (
          <div
            className={cn(
              "flex flex-col gap-[16px] w-full",
              "sm:flex-row sm:gap-[20px] md:gap-[40px]"
            )}
          >
            {sortedMetadata.map((item: MetaItem, index: number) => (
              <RevealOnScroll
                enableAnimation={isAnimationEnabled}
                y={100 * (index + 0.2)}
                key={item.id ?? index}
              >
              <div
                key={item.id ?? index}
                className={cn(
                  "flex flex-col gap-[17px] flex-1 min-w-0",
                  "sm:flex-row sm:gap-[16px] sm:items-start",
                  "lg:gap-[24px]"
                )}
              >
                {/* Mobile: icon + title in one row; Desktop: icon alongside title+desc column */}
                <div className={cn(
                  "flex gap-[16px] items-center w-full",
                  "sm:contents"
                )}>
                  {/* Icon */}
                  {item.meta_icon && (
                    <div className="flex items-center sm:py-[8px] lg:py-[10px] shrink-0">
                      <div className="relative w-[30px] h-[30px] lg:w-[40px] lg:h-[40px]">
                        <Image
                          src={item.meta_icon}
                          alt={item.meta_alt_icon || "Feature icon"}
                          fill
                          className="object-contain"
                          unoptimized
                        />
                      </div>
                    </div>
                  )}

                  {/* Mobile: title inline in same row as icon */}
                  {isMobile && hasFeatureTitle(item) && (
                    <p
                      className="flex-1 min-w-0 leading-140 text-[20px]"
                      style={{
                        fontFamily:
                          "var(--font-google-sans)",
                        fontWeight: 600,
                      }}
                    >
                      {getMetaTitleLine1(item) && (
                        <span
                          style={{
                            color:
                              item.meta_title_text_color_line_1 ||
                              item.meta_title_text_color ||
                              "#222222",
                          }}
                        >
                          {getMetaTitleLine1(item)}{" "}
                        </span>
                      )}
                      {item.meta_title_line_2 && (
                        <span
                          style={{
                            color:
                              item.meta_title_text_color_line_2 ||
                              item.meta_title_highlight_color ||
                              "#342B7C",
                          }}
                        >
                          {item.meta_title_line_2}
                        </span>
                      )}
                    </p>
                  )}
                </div>

                {/* Desktop: title + description column */}
                {!isMobile && (
                  <div className="flex flex-col gap-[15px] lg:gap-[24px] flex-1 min-w-0">
                    {hasFeatureTitle(item) && (
                      <div
                        className={cn(
                          "leading-140",
                          "text-[20px] md:text-[24px] lg:text-[28px]"
                        )}
                        style={{
                          fontFamily:
                            "var(--font-google-sans)",
                          fontWeight: 600,
                        }}
                      >
                        {getMetaTitleLine1(item) && (
                          <span
                            className="block"
                            style={{
                              color:
                                item.meta_title_text_color_line_1 ||
                                item.meta_title_text_color ||
                                "#222222",
                            }}
                          >
                            {getMetaTitleLine1(item)}
                          </span>
                        )}
                        {item.meta_title_line_2 && (
                          <span
                            className="block"
                            style={{
                              color:
                                item.meta_title_text_color_line_2 ||
                                item.meta_title_highlight_color ||
                                "#342B7C",
                            }}
                          >
                            {item.meta_title_line_2}
                          </span>
                        )}
                      </div>
                    )}
                    {item.meta_description && (
                      <p
                        className={cn(
                          "leading-[1.49]",
                          "text-[12px] md:text-[14px]",
                          "lg:text-[18px]"
                        )}
                        style={{
                          color:
                            item.meta_description_text_color ||
                            "rgba(34, 34, 34, 0.8)",
                          fontFamily: "var(--font-google-sans)",
                          fontWeight: 600,
                        }}
                      >
                        {item.meta_description}
                      </p>
                    )}
                  </div>
                )}

                {/* Mobile: description below icon+title row */}
                {isMobile && item.meta_description && (
                  <p
                    className="leading-[1.49] text-[12px] w-full"
                    style={{
                      color:
                        item.meta_description_text_color ||
                        "rgba(34, 34, 34, 0.8)",
                      fontFamily: "var(--font-google-sans)",
                      fontWeight: 600,
                    }}
                  >
                    {item.meta_description}
                  </p>
                )}
              </div>
              </RevealOnScroll>
            ))}
          </div>
        )}

        {/* CTA Button */}
        {cta_text && (
          <RevealOnScroll enableAnimation={isAnimationEnabled}>
          <div className="flex items-center justify-center">
            <a
              href={cta_type === "link" && cta_link ? cta_link : "#"}
              target={cta_type === "link" && cta_link ? "_blank" : undefined}
              rel={cta_type === "link" && cta_link ? "noopener noreferrer" : undefined}
              onClick={handleCtaClick}
              data-cta-name="apply_now"
              className={cn(
                "inline-flex items-center gap-[10px] font-semibold transition-opacity hover:opacity-90 cursor-pointer",
                "px-[24px] py-[12px] rounded-[4px]",
                "sm:py-[16px]"
              )}
              style={{
                color: cta_text_color,
                ...ctaBackgroundStyle,
              }}
            >
              <span
                className={cn(
                  "leading-[1.49] whitespace-nowrap",
                  "text-[14px]",
                  "sm:text-[16px] lg:text-[20px]"
                )}
                style={{
                  fontFamily: "var(--font-google-sans)",
                  fontWeight: 600,
                }}
              >
                {cta_text}
              </span>
              {cta_icon && (
                <span className="relative block w-[18px] h-[18px] shrink-0">
                  <Image
                    src={cta_icon}
                    alt={alt_cta_icon || "CTA icon"}
                    fill
                    className="object-contain"
                    unoptimized
                  />
                </span>
              )}
            </a>
          </div>
          </RevealOnScroll>
        )}
      </div>
    </section>
  );
};

export default DigitalLearning1;
